import React, { useState } from "react";
import { useLang } from "../contexts/LanguageContext";
import { submitContact } from "../lib/api";
import { toast } from "sonner";
import { Phone, Instagram, Music2, Facebook, Globe } from "lucide-react";

const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "", inquiry_type: "general" });
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitContact(form);
      toast.success(t.contact.sent);
      setForm({ name: "", company: "", email: "", phone: "", message: "", inquiry_type: "general" });
    } catch (err) {
      toast.error("Error: " + (err.response?.data?.detail || err.message));
    } finally { setSubmitting(false); }
  };

  const types = [
    { id: "sales", label: t.contact.types.sales },
    { id: "samples", label: t.contact.types.samples },
    { id: "partner", label: t.contact.types.partner },
    { id: "general", label: t.contact.types.general },
  ];

  const channels = [
    { icon: Phone, label: t.contact.phoneLabel, value: t.contact.phoneValue, href: `tel:${t.contact.phoneValue.replace(/\s/g, "")}`, testid: "channel-phone" },
    { icon: Instagram, label: "Instagram", value: t.contact.instagram, href: "https://instagram.com/thisisgarnacha", testid: "channel-instagram" },
    { icon: Music2, label: "TikTok", value: t.contact.tiktok, href: "https://tiktok.com/@thisisgarnacha", testid: "channel-tiktok" },
    { icon: Facebook, label: "Facebook", value: t.contact.facebook, href: "https://facebook.com/thisisgarnacha", testid: "channel-facebook" },
    { icon: Globe, label: "Website", value: t.contact.website, href: "https://www.garnacha.fi", testid: "channel-website" },
  ];

  return (
    <div data-testid="contact-page" className="pt-32 pb-32">
      <section className="container-pad pb-12">
        <div className="eyebrow text-[#F97316]">{t.contact.eyebrow}</div>
        <h1 className="display-serif mt-6 text-5xl sm:text-7xl lg:text-[7rem] leading-[0.95] tracking-tight">
          {t.contact.title}
        </h1>
        <p className="mt-8 max-w-xl text-lg text-foreground/70 font-light">{t.contact.sub}</p>
      </section>

      <section className="container-pad">
        <div className="grid lg:grid-cols-12 gap-16">
          {/* DIRECT CHANNELS */}
          <div className="lg:col-span-5 space-y-12">
            <div>
              <div className="eyebrow text-[#F97316] mb-6">{t.contact.directTitle}</div>
              <ul className="divide-y divide-border border-t border-b border-border" data-testid="contact-channels">
                {channels.map((c) => {
                  const Icon = c.icon;
                  return (
                    <li key={c.label}>
                      <a
                        href={c.href}
                        data-testid={c.testid}
                        target={c.href.startsWith("http") ? "_blank" : undefined}
                        rel={c.href.startsWith("http") ? "noopener noreferrer" : undefined}
                        className="flex items-center justify-between py-5 group"
                      >
                        <div className="flex items-center gap-4">
                          <Icon className="w-4 h-4 text-[#F97316]" />
                          <span className="eyebrow text-foreground/55">{c.label}</span>
                        </div>
                        <span className="text-base sm:text-lg group-hover:text-[#F97316] transition-colors">
                          {c.value}
                        </span>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div>
              <div className="eyebrow text-[#F97316] mb-4">{t.contact.type}</div>
              <div className="flex flex-wrap gap-2">
                {types.map((tp) => (
                  <button
                    key={tp.id}
                    data-testid={`contact-type-${tp.id}`}
                    type="button"
                    onClick={() => setForm({ ...form, inquiry_type: tp.id })}
                    className={`btn-pill text-xs ${form.inquiry_type === tp.id ? "bg-[#F97316] text-[#FFF8F0]" : "border border-border hover:border-foreground"}`}
                  >
                    {tp.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* FORM */}
          <form onSubmit={onSubmit} className="lg:col-span-7 lg:col-start-6 space-y-8" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-7">
              <input data-testid="contact-name" required placeholder={t.contact.name} className="input-clean" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} />
              <input data-testid="contact-company" placeholder={t.contact.company} className="input-clean" value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} />
              <input data-testid="contact-email" type="email" required placeholder={t.contact.email} className="input-clean" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} />
              <input data-testid="contact-phone" placeholder={t.contact.phone} className="input-clean" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} />
            </div>
            <textarea data-testid="contact-message" required rows={6} placeholder={t.contact.message} className="input-clean resize-none" value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} />
            <button data-testid="contact-submit-btn" type="submit" disabled={submitting} className="btn-primary text-base px-10 py-4 disabled:opacity-60 shadow-[0_8px_24px_-8px_rgba(249,115,22,0.55)]">
              {submitting ? "..." : t.contact.send}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Contact;
